import axios from "axios";
import { Toast, DialogPlugin } from "tdesign-mobile-vue";
import { refreshToken } from "@/utils/api.js";
import { goAuth } from "@/hooks/usePage.js";
import { useUserStore } from "@/store";



// 创建 axios 实例
let http = axios.create({
  baseURL: import.meta.env.MODE == "development" ? "/api" : import.meta.env.VITE_API_BASE_URL,
  withCredentials: false,
  loading: false,
  headers: {
    "Content-Type": "application/json",
  },
});

// 添加跳转锁，防止多次跳转
let isRedirecting = false;
// 是否正在刷新token
let isRefreshing = false;
// 重试队列，每一项是一个函数
let requests = [];

// 添加请求拦截器
http.interceptors.request.use(
  async (config) => {
    // 默认开启 infoShow，除非显式设为 false
    if (config.infoShow === undefined) {
      config.infoShow = true;
    }
    if (config.loading) {
      Toast({
        theme: "loading",
        preventScrollThrough: true,
        duration: 0,
      });
    }
    const userStore = useUserStore();
    if (userStore.getToken) {
      config.headers.Authorization = "Bearer " + userStore.getToken;
    }
    return config;
  },
  (error) => {
    // 请求错误处理
    return Promise.reject(error);
  },
);

// 添加响应拦截器
http.interceptors.response.use(
  (response) => {
    Toast.clear();
    if (response.data.code == 0 && response.config.infoShow) {
      if (response.data?.msg === "nosms") {
        return response.data
      }
      DialogPlugin.alert({
        title: "温馨提示",
        content: response.data?.msg,
        confirmBtn: "确定",
        onConfirm: () => {

        }
      });
    }
    return response.data;
  },
  async (error) => {
    const { response } = error;
    const { status, data } = response || {};
    const userStore = useUserStore();
    Toast.clear();

    // 处理 Token 过期 (402)
    if (status === 402) {
      const config = error.config;
      if (!isRefreshing) {
        isRefreshing = true;
        return refreshToken()
          .then((res) => {
            if (res.code == 1) {
              const newToken = res.data;
              userStore.setToken(newToken);

              // 刷新成功，执行队列中的请求
              requests.forEach((cb) => cb(newToken));
              requests = [];

              // 重试当前请求
              config.headers.Authorization = "Bearer " + newToken;
              return http(config);
            } else {
              return Promise.reject(res);
            }
          })
          .catch((err) => {
            console.error("refreshToken error", err);
            // 刷新失败，清空队列，跳转登录
            requests = [];
            if (userStore.isLogin) {
              window.sessionStorage.setItem("autoAuth", "true");
            }
            userStore.resetInfo();

            if (!isRedirecting) {
              isRedirecting = true;
              goAuth(false);
              setTimeout(() => {
                isRedirecting = false;
              }, 1000);
            }
            return Promise.reject(err);
          })
          .finally(() => {
            isRefreshing = false;
          });
      } else {
        // 正在刷新中，将请求挂起放入队列
        return new Promise((resolve) => {
          requests.push((token) => {
            config.headers.Authorization = "Bearer " + token;
            resolve(http(config));
          });
        });
      }
    }

    if (status === 403 || status === 401) {
      // 未授权或 Token 无效
      localStorage.setItem("authRedirectReason", "apiError");
      if (userStore.isLogin) {
        //以前登录过 有token 授权过
        window.sessionStorage.setItem("autoAuth", "true");
      }

      // 防止多次跳转
      if (!isRedirecting) {
        isRedirecting = true;
        userStore.resetInfo();
        goAuth(false);
        // 重置跳转锁，确保下次可以正常跳转
        setTimeout(() => {
          isRedirecting = false;
        }, 1000);
      }
      return Promise.reject(error);
    } else if (status === 505 && response.config.infoShow) {
      // 自定义错误码
      DialogPlugin.alert({
        title: "温馨提示",
        content: response.data.msg.replace(/\\n/g, "\n"),
        confirmBtn: "确定",
      });
    } else {
      const config = error.config || error.response?.config;
      if (config?.infoShow) {
        // 系统错误
        DialogPlugin.alert({
          title: "温馨提示",
          content: "系统错误",
          confirmBtn: "确定",
        });
      }
    }
    return Promise.reject(error);
  },
);

export default http;
